import type { AnyAction, Reducer } from '@reduxjs/toolkit';
import { combineReducers } from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-named-as-default
import produce from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import { LOCATION_CHANGE } from 'redux-first-history';

import { Reducer as AlertReducer } from '@webapp/account/src/resources/alert';
import { Reducer as ApiReducer } from '@webapp/account/src/resources/api';
import * as FileManager from '@webapp/account/src/resources/filemanager';
import * as Layout from '@webapp/account/src/resources/layout';
import * as Links from '@webapp/account/src/resources/links';
import * as Logic from '@webapp/account/src/resources/logic';
import * as LogicLinkParams from '@webapp/account/src/resources/logic-link-params';
import * as Mailing from '@webapp/account/src/resources/mailing';
import { MultiLangReducer } from '@webapp/account/src/resources/multi-lang/slice';
import * as MyOrders from '@webapp/account/src/resources/my-orders';
import * as Pages from '@webapp/account/src/resources/pages';
import * as Profile from '@webapp/account/src/resources/profile';
import * as Questions from '@webapp/account/src/resources/questions';
import { questionsInitial } from '@webapp/account/src/resources/questions';
import * as Reports from '@webapp/account/src/resources/reports';
import { reportsInitial } from '@webapp/account/src/resources/reports/state';
import * as Respondents from '@webapp/account/src/resources/respondents';
import * as Settings from '@webapp/account/src/resources/settings';
import * as Smsing from '@webapp/account/src/resources/smsing';
import * as Survey from '@webapp/account/src/resources/survey';
import { selectCurrentSurveyId } from '@webapp/account/src/resources/survey/selectors';
import { initialState as surveyInitial } from '@webapp/account/src/resources/survey/state';
import * as Tasks from '@webapp/account/src/resources/tasks';
import * as Users from '@webapp/account/src/resources/users';

import { ApiRespCode } from '@webapp/common/lib/api';

import type { States } from './states';

let isFirstRendering = true;

const clearState = (
    states: States,
    {
        payload: {
            location: { pathname },
        },
    }: AnyAction,
): States => {
    if (isFirstRendering) {
        isFirstRendering = false;
    } else {
        const match = /^\/account\/survey\/(\d+)/.exec(pathname);
        const surveyId = match ? +match[1] : null;
        const loadedSurveyId = selectCurrentSurveyId(states);

        if (surveyId && loadedSurveyId && loadedSurveyId !== surveyId) {
            return {
                ...states,
                Questions: cloneDeep(questionsInitial),
                Survey: cloneDeep(surveyInitial),
                Reports: cloneDeep(reportsInitial),
            };
        }
    }

    return states;
};

const crossSliceReducer = (state: States, action: AnyAction): States =>
    produce(state, (draft) => {
        const failed = action.type.endsWith('_FAIL') || action.type.endsWith('/rejected');
        const code = action.code || action?.payload?.code;
        const licenseEnd = code === ApiRespCode.ERR_END_LICENSE;

        if (failed && licenseEnd) {
            draft.Alert.showBlocked = true;
            return;
        }

        if (action.type === LOCATION_CHANGE) {
            return clearState(draft, action);
        }
    });

export const rootReducer = (routerReducer): Reducer => {
    const reducers = combinedReducer(routerReducer);
    return (state, action) => {
        const intermediateState = reducers(state, action);
        return crossSliceReducer(intermediateState, action);
    };
};

const combinedReducer = (routerReducer): Reducer =>
    combineReducers({
        router: routerReducer,
        Alert: AlertReducer,
        Api: ApiReducer,
        Layout: Layout.Reducer,
        Profile: Profile.Reducer,
        Survey: Survey.Reducer,
        Users: Users.Reducer,
        MyOrders: MyOrders.Reducer,
        Pages: Pages.Reducer,
        Settings: Settings.Reducer,
        Questions: Questions.Reducer,
        Mailing: Mailing.Reducer,
        Smsing: Smsing.Reducer,
        Links: Links.Reducer,
        Reports: Reports.Reducer,
        FileManager: FileManager.Reducer,
        Logic: Logic.Reducer,
        Tasks: Tasks.Reducer,
        LogicLinkParams: LogicLinkParams.Reducer,
        Respondents: Respondents.Reducer,
        MultiLang: MultiLangReducer,
    });
